import {
  Button,
  Copy,
  Flex,
  Loader,
  Square,
  Squish,
  SvgCheck,
  SvgExclamation,
} from '@grubhubprod/gh-cookbook-react/dist';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import {
  ISnackbarConfig,
  SnackbarStatus,
} from 'src/components/snackbar/SnackbarLauncher';
// it is used to calculate the total dismiss time for the snackbar
const DISMISS_DELAY_TIMING = 5000;
const DISMISS_ANIMATION_TIMING = 250;

export interface ISnackbarProps {
  config: ISnackbarConfig;
  onDismiss: (taskId: string) => void;
}

export interface ISnackbarState {
  isFadingOut: boolean;
}

export function Snackbar({ config, onDismiss }: ISnackbarProps): JSX.Element {
  const { isPhone } = useScreenSizes();
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Set timer for auto dismiss the notification
  if (config.status !== SnackbarStatus.IN_PROGRESS) {
    // 5s + 250ms (per animation)
    // (250ms) refers to $snackbar-animation-length-ms scss variable
    setTimeout(() => {
      handleOnDismiss();
    }, DISMISS_DELAY_TIMING + DISMISS_ANIMATION_TIMING);
  }

  /**
   * Set fading out state to fire animation and call onDismiss cb from props
   */
  function handleOnDismiss() {
    setIsFadingOut(true);
    setTimeout(() => {
      onDismiss(config.id);
    }, DISMISS_ANIMATION_TIMING);
  }

  function getIcon(status: SnackbarStatus): JSX.Element {
    switch (status) {
      case SnackbarStatus.IN_PROGRESS:
        return <Loader inverted />;
      case SnackbarStatus.SUCCESS:
        return (
          <Copy variant="body" color="untintedBackground">
            <SvgCheck size="24px" />
          </Copy>
        );
      case SnackbarStatus.ERROR:
        return (
          <Copy variant="body" color="untintedBackground">
            <SvgExclamation size="24px" />
          </Copy>
        );
      default:
        return (
          <Copy variant="body" color="untintedBackground">
            <SvgCheck size="24px" />
          </Copy>
        );
    }
  }

  function getBgColor() {
    if (config.status === SnackbarStatus.IN_PROGRESS) {
      return 'globals.stone70';
    }
    if (config.status === SnackbarStatus.SUCCESS) {
      return 'success';
    }
    return 'danger';
  }

  return (
    <Squish
      role="alertdialog"
      inset="x-sm"
      data-testid={`snackbar-${config.id}`}
    >
      <Flex
        alignItems="center"
        bg={getBgColor()}
        className={classnames('snackbar', 'active', {
          closing: isFadingOut,
        })}
        width={isPhone ? '350px' : '400px'}
      >
        <Square inset="x-sm" data-testid="snackbar-icon" minWidth="40px">
          {getIcon(config.status)}
        </Square>

        <Square inset="x-sm" flexGrow={1} data-testid="snackbar-msg">
          <Copy variant="body" color="untintedBackground">
            {config.message}
          </Copy>
        </Square>

        {config.status === SnackbarStatus.IN_PROGRESS && (
          <Square data-testid="snackbar-hide" inset="x-sm">
            <Button
              className="snackbar_hideButton"
              data-testid="dismiss-button"
              buttonSize="small"
              variant="tertiary"
              onClick={handleOnDismiss}
            >
              Hide
            </Button>
          </Square>
        )}
      </Flex>
    </Squish>
  );
}
